import { useEffect, useMemo, useState } from 'react';
import { Topbar } from '@/components/Topbar';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { pricingService, PricingCatalog as Catalog } from '@/services/pricing.service';
import {
  Coins, MessageCircle, Megaphone, Target, Music2, Mail, Users, Bot, Contact2, Send, Sparkles, Receipt,
} from 'lucide-react';
import toast from 'react-hot-toast';

const empty: Omit<Catalog, '_id'> = {
  currency: 'USD',
  baseMonthlyFee: 0,
  pricePerWhatsappAccount: 0,
  pricePerFacebookAccount: 0,
  pricePerGoogleAdsAccount: 0,
  pricePerTiktokAccount: 0,
  pricePerEmailAccount: 0,
  pricePerUser: 0,
  pricePerContactBlock: 0, contactBlockSize: 1000,
  pricePerMessageBlock: 0, messageBlockSize: 1000,
  pricePerAiCreditBlock: 0, aiCreditBlockSize: 1000,
  pricePerBotFlow: 0,
  notes: '',
};

type RateKey = keyof Pick<Catalog,
  | 'pricePerWhatsappAccount' | 'pricePerFacebookAccount' | 'pricePerGoogleAdsAccount'
  | 'pricePerTiktokAccount' | 'pricePerEmailAccount' | 'pricePerUser' | 'pricePerBotFlow'>;

const CHANNEL_RATES: { key: RateKey; label: string; icon: React.ReactNode }[] = [
  { key: 'pricePerWhatsappAccount',  label: 'WhatsApp number',   icon: <MessageCircle size={14} /> },
  { key: 'pricePerFacebookAccount',  label: 'Facebook account',  icon: <Megaphone size={14} /> },
  { key: 'pricePerGoogleAdsAccount', label: 'Google Ads account',icon: <Target size={14} /> },
  { key: 'pricePerTiktokAccount',    label: 'TikTok account',    icon: <Music2 size={14} /> },
  { key: 'pricePerEmailAccount',     label: 'Email inbox',       icon: <Mail size={14} /> },
];

const SEAT_RATES: { key: RateKey; label: string; icon: React.ReactNode }[] = [
  { key: 'pricePerUser',    label: 'User seat', icon: <Users size={14} /> },
  { key: 'pricePerBotFlow', label: 'Bot flow',  icon: <Bot size={14} /> },
];

/**
 * A representative client, used to show what a rate change actually costs.
 * Editing rates is abstract on its own — the bill on the right makes the
 * consequence visible without having to open a real company.
 */
const SAMPLE = {
  whatsappAccounts: 2, facebookAccounts: 1, googleAdsAccounts: 1, tiktokAccounts: 0,
  emailAccounts: 1, users: 3, botFlows: 2, contacts: 5000, messages: 10000, aiCredits: 1000,
};

export default function PricingCatalogPage() {
  const [form, setForm] = useState<Omit<Catalog, '_id'>>(empty);
  const [saved, setSaved] = useState<Omit<Catalog, '_id'> | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    pricingService.get().then((c) => {
      const { _id, ...rest } = c;
      setForm(rest); setSaved(rest);
    });
  }, []);

  const set = <K extends keyof typeof form>(k: K, v: typeof form[K]) => setForm((s) => ({ ...s, [k]: v }));
  const dirty = useMemo(() => !!saved && JSON.stringify(saved) !== JSON.stringify(form), [saved, form]);

  const submit = async () => {
    setBusy(true);
    try {
      await pricingService.update(form);
      setSaved(form);
      toast.success('Pricing saved');
    } catch { /* interceptor toasts */ } finally { setBusy(false); }
  };

  const money = (n: number) => `${form.currency} ${n.toFixed(2)}`;
  const blocks = (count: number, size: number) => (size > 0 ? Math.ceil(count / size) : 0);

  // Line items for the sample bill — same maths the quote endpoint runs.
  const bill = useMemo(() => {
    const lines: { label: string; amount: number }[] = [
      { label: 'Base fee', amount: form.baseMonthlyFee },
      { label: `${SAMPLE.whatsappAccounts} × WhatsApp number`, amount: SAMPLE.whatsappAccounts * form.pricePerWhatsappAccount },
      { label: `${SAMPLE.facebookAccounts} × Facebook account`, amount: SAMPLE.facebookAccounts * form.pricePerFacebookAccount },
      { label: `${SAMPLE.googleAdsAccounts} × Google Ads account`, amount: SAMPLE.googleAdsAccounts * form.pricePerGoogleAdsAccount },
      { label: `${SAMPLE.emailAccounts} × Email inbox`, amount: SAMPLE.emailAccounts * form.pricePerEmailAccount },
      { label: `${SAMPLE.users} × User seat`, amount: SAMPLE.users * form.pricePerUser },
      { label: `${SAMPLE.botFlows} × Bot flow`, amount: SAMPLE.botFlows * form.pricePerBotFlow },
      { label: `${SAMPLE.contacts.toLocaleString()} contacts`, amount: blocks(SAMPLE.contacts, form.contactBlockSize) * form.pricePerContactBlock },
      { label: `${SAMPLE.messages.toLocaleString()} messages`, amount: blocks(SAMPLE.messages, form.messageBlockSize) * form.pricePerMessageBlock },
      { label: `${SAMPLE.aiCredits.toLocaleString()} AI credits`, amount: blocks(SAMPLE.aiCredits, form.aiCreditBlockSize) * form.pricePerAiCreditBlock },
    ];
    return { lines, total: lines.reduce((s, l) => s + l.amount, 0) };
  }, [form]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <Topbar
        title="Pricing"
        breadcrumb="Customers / Pricing"
        right={
          <div className="flex items-center gap-3">
            {dirty && <span className="text-xs text-warning">Unsaved changes</span>}
            <Button disabled={busy || !dirty} onClick={submit}>{busy ? 'Saving…' : 'Save changes'}</Button>
          </div>
        }
      />
      <div className="grid grid-cols-1 gap-4 p-6 xl:grid-cols-[minmax(0,1fr)_320px]">
        <div className="space-y-4">
          <Section
            icon={<Coins size={15} />}
            title="Base"
            subtitle="Charged to every company on custom pricing, before any per-unit rates."
          >
            <div>
              <RateRow label="Currency">
                <span className="inline-block w-[132px]">
                  <Input
                    value={form.currency}
                    onChange={(e) => set('currency', e.target.value.toUpperCase().slice(0, 3))}
                    className="text-center font-medium uppercase tracking-wide"
                  />
                </span>
              </RateRow>
              <RateRow label="Base monthly fee">
                <MoneyInput currency={form.currency} value={form.baseMonthlyFee} onChange={(v) => set('baseMonthlyFee', v)} />
              </RateRow>
            </div>
          </Section>

          <Section icon={<MessageCircle size={15} />} title="Channels" subtitle="Per connected account, per month.">
            {CHANNEL_RATES.map((r) => (
              <RateRow key={r.key} label={r.label} icon={r.icon}>
                <MoneyInput currency={form.currency} value={form[r.key]} onChange={(v) => set(r.key, v)} />
              </RateRow>
            ))}
          </Section>

          <Section icon={<Users size={15} />} title="Seats and automation" subtitle="Per unit, per month.">
            {SEAT_RATES.map((r) => (
              <RateRow key={r.key} label={r.label} icon={r.icon}>
                <MoneyInput currency={form.currency} value={form[r.key]} onChange={(v) => set(r.key, v)} />
              </RateRow>
            ))}
          </Section>

          <Section
            icon={<Contact2 size={15} />}
            title="Volume"
            subtitle="Sold in blocks. A partial block rounds up, so 5,400 contacts bills as 6 blocks of 1,000."
          >
            <BlockRow
              icon={<Contact2 size={14} />} label="Contacts stored" currency={form.currency}
              price={form.pricePerContactBlock} size={form.contactBlockSize}
              onPrice={(v) => set('pricePerContactBlock', v)} onSize={(v) => set('contactBlockSize', v)}
            />
            <BlockRow
              icon={<Send size={14} />} label="Outbound messages / mo" currency={form.currency}
              price={form.pricePerMessageBlock} size={form.messageBlockSize}
              onPrice={(v) => set('pricePerMessageBlock', v)} onSize={(v) => set('messageBlockSize', v)}
            />
            <BlockRow
              icon={<Sparkles size={14} />} label="AI credits / mo" currency={form.currency}
              price={form.pricePerAiCreditBlock} size={form.aiCreditBlockSize}
              onPrice={(v) => set('pricePerAiCreditBlock', v)} onSize={(v) => set('aiCreditBlockSize', v)}
            />
          </Section>

          <Section icon={<Receipt size={15} />} title="Notes" subtitle="Internal only — never shown to clients.">
            <textarea
              value={form.notes ?? ''}
              onChange={(e) => set('notes', e.target.value)}
              rows={3}
              placeholder="Why these rates changed, who approved it, when to revisit."
              className="w-full rounded-lg border border-border bg-bg px-3 py-2 text-[13px] text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </Section>
        </div>

        {/* Consequence panel — what these rates bill for one representative client. */}
        <aside className="xl:sticky xl:top-6 xl:self-start">
          <div className="overflow-hidden rounded-xl border border-border bg-surface">
            <div className="border-b border-border px-4 py-3">
              <h3 className="text-sm font-semibold text-text-primary">A typical client</h3>
              <p className="mt-0.5 text-[11px] leading-4 text-text-muted">
                Two WhatsApp numbers, Facebook, Google Ads, an inbox, three seats, two bot flows.
              </p>
            </div>
            <dl className="px-4 py-2">
              {bill.lines.map((l) => (
                <div key={l.label} className="flex items-baseline justify-between gap-3 py-1.5">
                  <dt className="truncate text-[12.5px] text-text-muted">{l.label}</dt>
                  <dd className={l.amount > 0 ? 'shrink-0 text-[12.5px] tabular-nums text-text-primary' : 'shrink-0 text-[12.5px] tabular-nums text-text-muted'}>
                    {money(l.amount)}
                  </dd>
                </div>
              ))}
            </dl>
            <div className="flex items-baseline justify-between gap-3 border-t border-border bg-bg/40 px-4 py-3">
              <span className="text-[12px] font-medium text-text-muted">Total each month</span>
              <span className="font-display text-[19px] font-bold tabular-nums text-text-primary">{money(bill.total)}</span>
            </div>
          </div>
          {bill.total === 0 && (
            <p className="mt-2 px-1 text-[11px] leading-4 text-text-muted">
              Every rate is still zero, so custom-priced companies bill nothing. Set the rates above to start charging.
            </p>
          )}
        </aside>
      </div>
    </>
  );
}

const Section: React.FC<{ icon: React.ReactNode; title: string; subtitle?: string; children: React.ReactNode }> =
  ({ icon, title, subtitle, children }) => (
    <section className="rounded-xl border border-border bg-surface p-5">
      <div className="mb-3">
        <h3 className="inline-flex items-center gap-2 text-sm font-semibold text-text-primary">
          <span className="text-primary">{icon}</span>{title}
        </h3>
        {subtitle && <p className="mt-0.5 max-w-prose text-[11.5px] leading-4 text-text-muted">{subtitle}</p>}
      </div>
      {children}
    </section>
  );

/** Rate-card line: what you're charging for on the left, the rate on the right. */
const RateRow: React.FC<{ label: string; icon?: React.ReactNode; children: React.ReactNode }> = ({ label, icon, children }) => (
  <div className="flex items-center justify-between gap-4 border-b border-border/60 py-2 last:border-b-0">
    <span className="inline-flex min-w-0 items-center gap-2 text-[13px] text-text-primary">
      {icon && <span className="text-text-muted">{icon}</span>}
      <span className="truncate">{label}</span>
    </span>
    <span className="shrink-0">{children}</span>
  </div>
);

/**
 * Width lives on the wrapper, not the input: Input's base class carries
 * `w-full`, which beats any w-* passed through className (Tailwind orders
 * w-full after fixed widths in the stylesheet, so specificity ties go to it).
 */
const MoneyInput: React.FC<{ currency: string; value: number; onChange: (v: number) => void }> = ({ currency, value, onChange }) => (
  <span className="relative inline-block w-[132px]">
    <span className="pointer-events-none absolute left-2.5 top-1/2 z-10 -translate-y-1/2 text-[11px] font-medium text-text-muted">
      {currency}
    </span>
    <Input
      type="number" min={0} step="0.01" value={value}
      onChange={(e) => onChange(Number(e.target.value) || 0)}
      className="pl-11 pr-2.5 text-right tabular-nums"
    />
  </span>
);

const BlockRow: React.FC<{
  icon: React.ReactNode; label: string; currency: string;
  price: number; size: number; onPrice: (v: number) => void; onSize: (v: number) => void;
}> = ({ icon, label, currency, price, size, onPrice, onSize }) => (
  <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border/60 py-2 last:border-b-0">
    <span className="inline-flex min-w-0 items-center gap-2 text-[13px] text-text-primary">
      <span className="text-text-muted">{icon}</span>
      <span className="truncate">{label}</span>
    </span>
    <span className="flex shrink-0 items-center gap-2">
      <MoneyInput currency={currency} value={price} onChange={onPrice} />
      <span className="text-[12px] text-text-muted">per</span>
      <span className="inline-block w-[88px]">
        <Input
          type="number" min={1} step={1} value={size}
          onChange={(e) => onSize(Math.max(1, Math.floor(Number(e.target.value) || 1)))}
          className="px-2.5 text-right tabular-nums"
        />
      </span>
    </span>
  </div>
);
